import React, { useEffect, useState } from 'react';
import { Search, X, CheckCircle } from 'lucide-react';
import { businessAdminService, businessSupplierService, businessTicketService } from '../../../services/businessApi';
import { useAuth } from '../../../context/AuthContext';
import '../Checklist/Checklist.css';
import '../../../styles/businessTheme.css';
import './NewBusinessTicket.css';

const initialForm = {
  plantId: '', categoryId: '', title: '', description: '',
  reportedByName: '', reportedByEmail: '', reportedByPhone: '',
};
const initialNewSupplier = { name: '', vendorCode: '', contactPerson: '', contactEmail: '', contactPhone: '' };

// Coordinator manual entry (Task 6.3) — business_support_lead only, e.g. for phone-in suppliers.
export default function NewBusinessTicket() {
  const { isBusinessLead } = useAuth();
  const [plants, setPlants] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(initialForm);

  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierResults, setSupplierResults] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showNewSupplier, setShowNewSupplier] = useState(false);
  const [newSupplier, setNewSupplier] = useState(initialNewSupplier);

  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ticketNumber, setTicketNumber] = useState(null);

  useEffect(() => {
    businessAdminService.listPlants().then(res => setPlants(res.data.plants.filter(p => p.isActive)));
    businessAdminService.listCategories().then(res => setCategories(res.data.categories.filter(c => c.isActive)));
  }, []);

  useEffect(() => {
    if (selectedSupplier || supplierQuery.trim().length < 2) { setSupplierResults([]); return; }
    const t = setTimeout(() => {
      businessSupplierService.search(supplierQuery).then(res => setSupplierResults(res.data.suppliers));
    }, 300);
    return () => clearTimeout(t);
  }, [supplierQuery, selectedSupplier]);

  if (!isBusinessLead) return <div className="empty-state">Only business support coordinators can log tickets manually.</div>;

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const updateNewSupplier = (field) => (e) => setNewSupplier(s => ({ ...s, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSupplier && !showNewSupplier) {
      setError('Select an existing supplier or add a new one');
      return;
    }
    if (showNewSupplier && !newSupplier.name.trim()) {
      setError('New supplier name is required');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        supplierId: selectedSupplier?._id,
        newSupplier: showNewSupplier ? newSupplier : undefined,
      };
      const res = await businessTicketService.createManual(payload);
      setTicketNumber(res.data.ticket.ticketNumber);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(initialForm);
    setSelectedSupplier(null);
    setShowNewSupplier(false);
    setNewSupplier(initialNewSupplier);
    setSupplierQuery('');
    setTicketNumber(null);
  };

  if (ticketNumber) {
    return (
      <div className="new-business-ticket-page biz-theme biz-page">
        <div className="card new-business-ticket-confirm">
          <CheckCircle size={28} color="var(--success)" />
          <h2>Ticket {ticketNumber} created</h2>
          <button className="btn btn-primary" onClick={reset}>Log another ticket</button>
        </div>
      </div>
    );
  }

  return (
    <div className="new-business-ticket-page biz-theme biz-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">New Business Ticket</h1>
          <p className="page-subtitle">Manual entry for phone-in or email-relayed issues</p>
        </div>
      </div>

      <div className="card">
        <form className="new-business-ticket-form" onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-section-title">Supplier</div>
          {selectedSupplier ? (
            <div className="supplier-selected">
              <div>
                <div className="checklist-supplier-name">{selectedSupplier.name}</div>
                <div className="checklist-supplier-code">{selectedSupplier.vendorCode}</div>
              </div>
              <button type="button" className="icon-btn-sm" onClick={() => setSelectedSupplier(null)}><X size={14} /></button>
            </div>
          ) : !showNewSupplier ? (
            <>
              <div className="search-box">
                <Search size={14} />
                <input
                  placeholder="Search supplier by name or vendor code..."
                  value={supplierQuery}
                  onChange={e => setSupplierQuery(e.target.value)}
                />
              </div>
              {supplierResults.length > 0 && (
                <div className="supplier-results">
                  {supplierResults.map(s => (
                    <div key={s._id} className="supplier-result-row" onClick={() => { setSelectedSupplier(s); setSupplierResults([]); }}>
                      <span>{s.name}</span>
                      <span className="checklist-supplier-code">{s.vendorCode}</span>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowNewSupplier(true)}>
                + Can't find them — add new supplier
              </button>
            </>
          ) : (
            <div className="new-supplier-fields">
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input value={newSupplier.name} onChange={updateNewSupplier('name')} required />
                </div>
                <div className="form-group">
                  <label>Vendor code (if known)</label>
                  <input value={newSupplier.vendorCode} onChange={updateNewSupplier('vendorCode')} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact person</label>
                  <input value={newSupplier.contactPerson} onChange={updateNewSupplier('contactPerson')} />
                </div>
                <div className="form-group">
                  <label>Contact phone</label>
                  <input value={newSupplier.contactPhone} onChange={updateNewSupplier('contactPhone')} />
                </div>
              </div>
              <div className="form-group">
                <label>Contact email</label>
                <input type="email" value={newSupplier.contactEmail} onChange={updateNewSupplier('contactEmail')} />
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowNewSupplier(false)}>
                Cancel — search existing instead
              </button>
            </div>
          )}

          <div className="form-section-title">Ticket</div>
          <div className="form-row">
            <div className="form-group">
              <label>Plant</label>
              <select value={form.plantId} onChange={update('plantId')} required>
                <option value="">Select plant...</option>
                {plants.map(p => <option key={p._id} value={p._id}>{p.name} ({p.code})</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Category</label>
              <select value={form.categoryId} onChange={update('categoryId')} required>
                <option value="">Select category...</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Title</label>
            <input value={form.title} onChange={update('title')} required />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea rows={4} value={form.description} onChange={update('description')} required />
          </div>

          <div className="form-section-title">Reported by</div>
          <div className="form-row">
            <div className="form-group">
              <label>Name</label>
              <input value={form.reportedByName} onChange={update('reportedByName')} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={form.reportedByPhone} onChange={update('reportedByPhone')} />
            </div>
          </div>
          <div className="form-group">
            <label>Email</label>
            <input type="email" value={form.reportedByEmail} onChange={update('reportedByEmail')} />
          </div>

          <div className="modal-footer" style={{ justifyContent: 'flex-start' }}>
            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
